import { useCallback, type ComponentPropsWithoutRef, type Ref } from 'react';
import { Card, Skeleton } from '@monksflow/monks-ui';
import { useNavigate } from 'react-router';

import { ToolCardV4 as ToolCard } from '@/shared/ui';
import { EmptyState } from '@/shared/ui/EmptyState';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { usePermissions } from '@/entities/user';
import { ROUTES } from '@/shared/config/routes';

import { useSpringboardTools } from '../../model';
import { useManagedTools } from '../../model/useManagedTools';
import type { ToolGridProps } from './ToolGrid.types';
import { toolGridV4Variants, type ToolGridV4VariantProps } from './ToolGrid.v4.variants';
import { useTranslation } from 'react-i18next';

/**
 * V4 native sibling for `<ToolGrid>`.
 *
 * Pure semantic HTML + Tailwind. Same prop interface as the v3 sibling.
 * Shadcn-compliant: `ref` as a plain prop (React 19), `className?` via tv(),
 * `data-slot="tool-grid"`, `displayName`. Reuses the shared V4 ToolCard and the
 * shared EmptyState.
 *
 * Token mapping (per project glass/shadow deprecation):
 *   - v3 MUI `<Skeleton>`                             → `Skeleton` from `@monksflow/monks-ui`
 *   - v3 MUI `<Box>`                                  → semantic `<div>` + Tailwind
 *   - v3 `theme.spacing(N)`                           → Tailwind `gap-{N}` / `p-{N}`
 *   - v3 `surface.glass1` (skeleton background)       → V4 NATIVE `bg-card` (flat)
 *   - v3 `borderRadius: '24px'`                       → `rounded-3xl` (24px in Tailwind 4)
 */

export interface ToolGridV4Props
  extends
    Omit<ComponentPropsWithoutRef<'div'>, keyof ToolGridProps>,
    ToolGridProps,
    ToolGridV4VariantProps {
  /** React 19 ref-as-prop: forwarded to the underlying `<div>`. */
  ref?: Ref<HTMLDivElement>;
}

export function ToolGridV4({
  selectedCategory,
  selectedTab,
  showFlowDigest,
  workspaceSlug,
  onToolClick,
  className,
  ref,
  ...rest
}: ToolGridV4Props) {
  const { t } = useTranslation('springboard');

  const navigate = useNavigate();
  const workspaceId = useCurrentWorkspaceId();

  const { canManageTools } = usePermissions();
  const {
    tools,
    isLoading: isToolsLoading,
    isError,
  } = useSpringboardTools({
    tabId: selectedTab?.id,
    enabled: !!workspaceId,
  });
  const { tools: managedTools, isLoading: isManagedLoading } = useManagedTools({
    showFlowDigest,
    workspaceSlug,
  });
  const categoryManagedTools = managedTools.filter((tool) => tool.category === selectedTab?.id);
  const isLoading = isToolsLoading || isManagedLoading;

  const selectTool = useCallback(
    (agentId: string) => {
      onToolClick(agentId);
    },
    [onToolClick],
  );

  if (!selectedTab) return null;

  if (isError) {
    return (
      <EmptyState
        title={t('springboard.toolGridV4.failedToLoadTools')}
        description={t('springboard.toolGridV4.pleaseTryAgainLater')}
      />
    );
  }

  const hasAnyTools = tools.length > 0 || categoryManagedTools.length > 0;

  if (!isLoading && !hasAnyTools && selectedCategory === 'all') return null;

  if (!isLoading && !hasAnyTools) {
    return (
      <div ref={ref} data-slot="tool-grid" className={toolGridV4Variants({ className })} {...rest}>
        <div className="col-span-full">
          <EmptyState
            title={t('springboard.toolGridV4.noToolsAvailable')}
            description={t('springboard.toolGridV4.pleaseContactYourAdministratorTo')}
          />
        </div>
      </div>
    );
  }

  return (
    <div ref={ref} data-slot="tool-grid" className={toolGridV4Variants({ className })} {...rest}>
      {!isLoading &&
        categoryManagedTools.map((tool) => (
          <ToolCard
            agentId={tool.id}
            tag={tool.category}
            key={tool.id}
            title={tool.title}
            description={tool.description}
            onClick={tool.onOpen}
          />
        ))}
      {isLoading &&
        Array.from({ length: 4 }).map((_, index) => (
          <Card
            key={`tool-skeleton-${index}`}
            variant="outline"
            className="gap-6 rounded-lg border-border-light p-4 shadow-none"
          >
            <Skeleton className="size-12 rounded-sm" data-testid="tool-skeleton-loader" />
            <div className="flex flex-col gap-1">
              <Skeleton className="w-4/5 h-6" data-testid="tool-skeleton-loader" />
              <Skeleton className="w-3/5 h-4" data-testid="tool-skeleton-loader" />
            </div>
          </Card>
        ))}
      {!isLoading &&
        tools.map((tool) => {
          const canConfigure = tool.isUnconfigured && canManageTools;

          return (
            <ToolCard
              agentId={tool.id}
              tag={selectedTab.id}
              key={tool.id}
              title={tool.title}
              onClick={
                canConfigure
                  ? () => void navigate(ROUTES.ADMIN_AGENT_CONFIG(tool.id))
                  : () => selectTool(tool.id)
              }
              description={tool.description}
              {...(tool.visibility ? { visibility: tool.visibility } : {})}
              disabled={tool.disabled && !canConfigure}
              disabledReason={tool.disabled && !canConfigure ? tool.disabledReason : undefined}
              disabledTooltip={
                tool.disabled && !canConfigure && tool.isUnconfigured
                  ? t('springboard.toolGridV4.thisToolNeedsToBe')
                  : undefined
              }
              statusLabel={canConfigure ? t('springboard.toolGridV4.notConfigured') : undefined}
            />
          );
        })}
    </div>
  );
}

ToolGridV4.displayName = 'ToolGridV4';
